<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  authorizeSufficit,
  connectSufficit,
  createSufficitInbox,
  DEFAULT_SUFFICIT_API_URL,
  DEFAULT_SUFFICIT_CONTEXTS_ENDPOINT,
  DEFAULT_SUFFICIT_EXTENSIONS_ENDPOINT_TEMPLATE,
  DEFAULT_SUFFICIT_IDENTITY_URL,
  DEFAULT_SUFFICIT_OAUTH_CLIENT_ID,
  DEFAULT_SUFFICIT_PORTAL_URL,
  disconnectSufficit,
  fetchSufficitBrowserContexts,
  fetchSufficitState,
  refreshSufficitContexts,
} from '../../../api/sufficit';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const isLoading = ref(false);
const isAuthorizing = ref(false);
const isSaving = ref(false);
const isRefreshing = ref(false);
const isDisconnecting = ref(false);
const creatingInboxId = ref(null);
const state = ref({
  connected: false,
  hook: null,
  contexts: [],
  linked_inboxes: [],
});

const form = reactive({
  identity_url: DEFAULT_SUFFICIT_IDENTITY_URL,
  oauth_client_id: DEFAULT_SUFFICIT_OAUTH_CLIENT_ID,
  site_url: DEFAULT_SUFFICIT_API_URL,
  portal_url: DEFAULT_SUFFICIT_PORTAL_URL,
  contexts_endpoint: DEFAULT_SUFFICIT_CONTEXTS_ENDPOINT,
  extensions_endpoint_template: DEFAULT_SUFFICIT_EXTENSIONS_ENDPOINT_TEMPLATE,
});

const accountId = computed(() => route.params.accountId);
const linkedInboxes = computed(() => state.value.linked_inboxes || []);
const contexts = computed(() => state.value.contexts || []);
const connected = computed(() => Boolean(state.value.connected));
const syncError = computed(
  () => state.value.hook?.last_context_sync_error || ''
);

const consumeOauthQueryFeedback = async () => {
  const status = route.query.oauth_status;
  const message = route.query.oauth_message;

  if (!status) {
    return;
  }

  if (status === 'success') {
    useAlert(t('SUFFICIT_INTEGRATION.CONNECTION.OAUTH_SUCCESS'));
  } else if (status === 'warning') {
    useAlert(message || t('SUFFICIT_INTEGRATION.CONNECTION.OAUTH_WARNING'));
  } else if (status === 'error') {
    useAlert(message || 'Unable to complete the Sufficit OAuth flow.');
  }

  const query = { ...route.query };
  delete query.oauth_status;
  delete query.oauth_message;

  await router.replace({ query });
};

const loadContextsFromBrowserSession = async () => {
  if (!form.site_url) {
    throw new Error('Configure the Sufficit API URL first.');
  }

  return fetchSufficitBrowserContexts({
    siteUrl: form.site_url,
    contextsEndpoint: form.contexts_endpoint,
  });
};

const formatContextConnection = context => {
  return `${context.sip_server}:${context.sip_port} · ${context.sip_transport.toUpperCase()}`;
};

const formatContextRealm = context => {
  return `${context.sip_realm}`;
};

const formatLinkedInboxSummary = linkedInbox => {
  return `${linkedInbox.context_name} · ${linkedInbox.sip_server}:${linkedInbox.sip_port}`;
};

const hydrateForm = hook => {
  if (!hook) {
    return;
  }

  form.site_url = hook.site_url || form.site_url;
  form.portal_url =
    hook.portal_url || hook.identity_url || hook.site_url || form.portal_url;
  form.identity_url = hook.identity_url || form.identity_url;
  form.oauth_client_id = hook.oauth_client_id || form.oauth_client_id;
  form.contexts_endpoint = hook.contexts_endpoint || form.contexts_endpoint;
  form.extensions_endpoint_template =
    hook.extensions_endpoint_template || form.extensions_endpoint_template;
};

const loadState = async () => {
  isLoading.value = true;
  try {
    const response = await fetchSufficitState(accountId.value);
    state.value = response.data;
    hydrateForm(response.data?.hook);
    await store.dispatch('integrations/get');
  } catch (error) {
    useAlert(
      error.response?.data?.message || 'Unable to load Sufficit integration.'
    );
  } finally {
    isLoading.value = false;
  }
};

const startOauthConnection = async () => {
  isAuthorizing.value = true;

  try {
    const response = await authorizeSufficit(accountId.value, {
      ...form,
      auth_mode: 'oauth',
    });

    const url = response.data?.url;
    if (!url) {
      throw new Error('Unable to start the Sufficit OAuth flow.');
    }

    window.location.assign(url);
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        error.message ||
        'Unable to start the Sufficit OAuth flow.'
    );
    isAuthorizing.value = false;
  }
};

const saveConnection = async () => {
  isSaving.value = true;
  try {
    const remoteContexts = await loadContextsFromBrowserSession();
    const response = await connectSufficit(accountId.value, {
      ...form,
      auth_mode: 'browser_session',
      contexts: remoteContexts,
    });

    state.value = response.data;
    hydrateForm(response.data?.hook);
    await store.dispatch('integrations/get');
    useAlert('Sufficit connected using the current browser session.');
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        error.message ||
        'Unable to connect to Sufficit.'
    );
  } finally {
    isSaving.value = false;
  }
};

const refreshContexts = async () => {
  isRefreshing.value = true;
  try {
    let payload = {
      ...form,
    };

    if (state.value?.hook?.auth_mode === 'oauth') {
      payload.auth_mode = 'oauth';
    } else {
      const remoteContexts = await loadContextsFromBrowserSession();
      payload = {
        ...payload,
        auth_mode: 'browser_session',
        contexts: remoteContexts,
      };
    }

    const response = await refreshSufficitContexts(accountId.value, payload);
    state.value = response.data;
    useAlert('Contexts refreshed successfully.');
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        error.message ||
        'Unable to refresh Sufficit contexts.'
    );
  } finally {
    isRefreshing.value = false;
  }
};

const disconnectIntegration = async () => {
  isDisconnecting.value = true;
  try {
    await disconnectSufficit(accountId.value);
    state.value = {
      connected: false,
      hook: null,
      contexts: [],
      linked_inboxes: [],
    };
    await store.dispatch('integrations/get');
    useAlert('Sufficit disconnected.');
  } catch (error) {
    useAlert(error.response?.data?.message || 'Unable to disconnect Sufficit.');
  } finally {
    isDisconnecting.value = false;
  }
};

const createInboxForContext = async context => {
  creatingInboxId.value = context.id;

  try {
    const response = await createSufficitInbox(accountId.value, {
      context_id: context.id,
      inbox_name: context.inbox_name || context.name,
    });

    useAlert(`Inbox ${response.data.inbox_name} created successfully.`);
    await loadState();
    await router.push(
      frontendURL(
        `accounts/${accountId.value}/settings/inboxes/${response.data.inbox_id}/collaborators`
      )
    );
  } catch (error) {
    useAlert(
      error.response?.data?.message || 'Unable to create the SIP inbox.'
    );
  } finally {
    creatingInboxId.value = null;
  }
};

const openPortal = () => {
  const url = form.portal_url || form.site_url;
  if (!url) {
    useAlert('Configure the Sufficit portal URL first.');
    return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
};

onMounted(async () => {
  await consumeOauthQueryFeedback();
  await loadState();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('SUFFICIT_INTEGRATION.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('SUFFICIT_INTEGRATION.TITLE')"
        :description="$t('SUFFICIT_INTEGRATION.DESCRIPTION')"
        :back-button-label="$t('SUFFICIT_INTEGRATION.BACK')"
        icon-name="i-ph-plugs-connected"
      >
        <template #actions>
          <NextButton
            variant="outline"
            color="slate"
            :label="$t('SUFFICIT_INTEGRATION.OPEN_PORTAL')"
            @click="openPortal"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <SettingsSection
        :title="$t('SUFFICIT_INTEGRATION.CONNECTION.TITLE')"
        :sub-title="$t('SUFFICIT_INTEGRATION.CONNECTION.SUBTITLE')"
      >
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('SUFFICIT_INTEGRATION.CONNECTION.SITE_URL') }}
            </span>
            <input
              v-model="form.site_url"
              type="url"
              class="w-full rounded border border-n-weak bg-n-alpha-3 px-3 py-2 text-sm"
              :placeholder="
                $t('SUFFICIT_INTEGRATION.CONNECTION.SITE_URL_PLACEHOLDER')
              "
            />
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('SUFFICIT_INTEGRATION.CONNECTION.PORTAL_URL') }}
            </span>
            <input
              v-model="form.portal_url"
              type="url"
              class="w-full rounded border border-n-weak bg-n-alpha-3 px-3 py-2 text-sm"
              :placeholder="
                $t('SUFFICIT_INTEGRATION.CONNECTION.PORTAL_URL_PLACEHOLDER')
              "
            />
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('SUFFICIT_INTEGRATION.CONNECTION.CONTEXTS_ENDPOINT') }}
            </span>
            <input
              v-model="form.contexts_endpoint"
              type="text"
              class="w-full rounded border border-n-weak bg-n-alpha-3 px-3 py-2 text-sm"
            />
          </label>

          <label class="flex flex-col gap-1 md:col-span-2">
            <span class="text-xs font-medium text-n-slate-11">
              {{
                $t(
                  'SUFFICIT_INTEGRATION.CONNECTION.EXTENSIONS_ENDPOINT_TEMPLATE'
                )
              }}
            </span>
            <input
              v-model="form.extensions_endpoint_template"
              type="text"
              class="w-full rounded border border-n-weak bg-n-alpha-3 px-3 py-2 text-sm"
            />
          </label>
        </div>

        <p class="mt-3 mb-0 text-sm text-n-slate-11">
          {{ $t('SUFFICIT_INTEGRATION.CONNECTION.BROWSER_SESSION_HINT') }}
        </p>
        <p class="mt-2 mb-0 text-xs text-n-slate-10">
          {{
            $t('SUFFICIT_INTEGRATION.CONNECTION.IDENTITY_HINT', {
              identityUrl: form.identity_url,
              clientId: form.oauth_client_id,
            })
          }}
        </p>

        <div
          v-if="syncError"
          class="mt-3 rounded border border-n-warning/40 bg-n-warning/10 px-3 py-2 text-sm text-n-slate-12"
        >
          <strong>{{
            $t('SUFFICIT_INTEGRATION.CONNECTION.SYNC_ERROR_TITLE')
          }}</strong>
          {{ syncError }}
        </div>

        <div class="mt-4 flex flex-wrap gap-2">
          <NextButton
            :is-loading="isAuthorizing"
            :label="$t('SUFFICIT_INTEGRATION.CONNECTION.CONNECT_OAUTH')"
            @click="startOauthConnection"
          />
          <NextButton
            :is-loading="isSaving"
            variant="outline"
            color="slate"
            :label="$t('SUFFICIT_INTEGRATION.CONNECTION.SAVE')"
            @click="saveConnection"
          />
          <NextButton
            v-if="connected"
            variant="outline"
            color="slate"
            :is-loading="isRefreshing"
            :label="$t('SUFFICIT_INTEGRATION.CONNECTION.REFRESH')"
            @click="refreshContexts"
          />
          <NextButton
            v-if="connected"
            variant="outline"
            color="ruby"
            :is-loading="isDisconnecting"
            :label="$t('SUFFICIT_INTEGRATION.CONNECTION.DISCONNECT')"
            @click="disconnectIntegration"
          />
        </div>
      </SettingsSection>

      <SettingsSection
        :title="$t('SUFFICIT_INTEGRATION.CONTEXTS.TITLE')"
        :sub-title="$t('SUFFICIT_INTEGRATION.CONTEXTS.SUBTITLE')"
      >
        <div
          v-if="!contexts.length"
          class="rounded border border-dashed border-n-weak px-4 py-6 text-sm text-n-slate-11"
        >
          {{ $t('SUFFICIT_INTEGRATION.CONTEXTS.EMPTY') }}
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="context in contexts"
            :key="context.id"
            class="rounded border border-n-weak bg-n-alpha-3 p-4"
          >
            <div
              class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
            >
              <div class="space-y-1">
                <p class="mb-0 text-base font-medium text-n-slate-12">
                  {{ context.name }}
                </p>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{ formatContextConnection(context) }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{
                    $t('SUFFICIT_INTEGRATION.CONTEXTS.REALM_VALUE', {
                      value: formatContextRealm(context),
                    })
                  }}
                </p>
              </div>

              <NextButton
                :is-loading="creatingInboxId === context.id"
                :label="$t('SUFFICIT_INTEGRATION.CONTEXTS.CREATE_INBOX')"
                @click="createInboxForContext(context)"
              />
            </div>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        :title="$t('SUFFICIT_INTEGRATION.LINKED_INBOXES.TITLE')"
        :sub-title="$t('SUFFICIT_INTEGRATION.LINKED_INBOXES.SUBTITLE')"
        :show-border="false"
      >
        <div
          v-if="!linkedInboxes.length"
          class="rounded border border-dashed border-n-weak px-4 py-6 text-sm text-n-slate-11"
        >
          {{ $t('SUFFICIT_INTEGRATION.LINKED_INBOXES.EMPTY') }}
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="linkedInbox in linkedInboxes"
            :key="linkedInbox.id"
            class="rounded border border-n-weak bg-n-alpha-3 p-4"
          >
            <div
              class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
            >
              <div class="space-y-1">
                <p class="mb-0 text-base font-medium text-n-slate-12">
                  {{ linkedInbox.name }}
                </p>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{ formatLinkedInboxSummary(linkedInbox) }}
                </p>
              </div>

              <NextButton
                variant="outline"
                color="slate"
                :label="$t('SUFFICIT_INTEGRATION.LINKED_INBOXES.OPEN')"
                @click="
                  router.push(
                    frontendURL(
                      `accounts/${accountId}/settings/inboxes/${linkedInbox.id}/collaborators`
                    )
                  )
                "
              />
            </div>
          </div>
        </div>
      </SettingsSection>
    </template>
  </SettingsLayout>
</template>
